import { ReactNode, createContext, useContext } from 'react';
import { getChildrenByType } from '../../utils';
import { Link } from 'react-router-dom';
import Text from '../text/Text';
import ButtonV2 from '../button/ButtonV2';

const containerStyle: { [key: string]: string } = {
    primary: 'bg-indigo-50 border-indigo-400',
    info: 'bg-blue-50 border-blue-400',
    success: 'bg-green-50 border-green-400',
    warn: 'bg-yellow-50 border-yellow-400',
    error: 'bg-red-50 border-red-400',
};

const textColor: { [key: string]: string } = {
    primary: 'text-indigo-600',
    info: 'text-blue-600',
    success: 'text-green-600',
    warn: 'text-yellow-600',
    error: 'text-red-600',
};

export type ActionBannerProps = {
    /**
     * Variant of the banner
     */
    variant?: 'primary' | 'info' | 'success' | 'warn' | 'error';
    /**
     * Icon besides the title
     */
    decoration: ReactNode;
    /**
     * Banner title
     */
    title: string;
    /**
     * Action buttons to be rendered
     */
    children: ReactNode | ReactNode[];
};

const ActionBannerContext = createContext<Pick<ActionBannerProps, 'variant'>>({ variant: 'primary' });

function ActionBanner({ variant = 'primary', decoration, title, children }: ActionBannerProps): JSX.Element {
    const primaryAction = getChildrenByType(children, PrimaryAction);
    const secondaryAction = getChildrenByType(children, SecondaryAction);

    return (
        <div className={`w-full py-2 px-4 border-2 ${containerStyle[variant]} rounded-lg flex items-center justify-between`}>
            <div className="flex items-center gap-2 grow">
                <div className={`${textColor[variant]} shrink-0 w-5 h-5`}>{decoration}</div>
                <Text className="text-sm font-medium text-gray-900">{title}</Text>
            </div>
            <ActionBannerContext.Provider value={{ variant }}>
                <div className="flex items-center gap-2 shrink-0">
                    {secondaryAction}
                    {primaryAction}
                </div>
            </ActionBannerContext.Provider>
        </div>
    );
}

type PrimaryActionProps = {
    /**
     * Button icon
     */
    icon?: ReactNode;
    /**
     * Button label
     */
    label: string;
    /**
     * Callback when button is clicked
     */
    onClick: () => void;
};

function PrimaryAction({ icon, label, onClick }: PrimaryActionProps): JSX.Element {
    const { variant } = useContext(ActionBannerContext);

    let buttonType: 'primary' | 'danger' | 'secondary' | 'hollow' = 'primary';

    switch (variant) {
        case 'error':
            buttonType = 'danger';
            break;
        case 'warn':
        case 'success':
            buttonType = 'secondary';
            break;
        default:
            buttonType = 'primary';
    }

    return <ButtonV2 type={buttonType} leftIcon={icon} label={label} size="xs" onClick={onClick} />;
}

type SecondaryActionProps = {
    /**
     * Button label
     */
    label: string;
    /**
     * Link to open up when button is clicked
     */
    link?: string;
    /**
     * Callback when button is clicked
     */
    onClick?: () => void;
};

function isExternalLink(link: string): boolean | undefined {
    try {
        // eslint-disable-next-line no-unused-vars
        const url = new URL(link);
        return true;
    } catch {
        return false;
    }
}

function SecondaryAction({ label, link, onClick }: SecondaryActionProps): JSX.Element {
    const button = <ButtonV2 type="hollow" label={label} size="xs" onClick={onClick} />;

    if (link) {
        if (isExternalLink(link)) {
            return (
                <a href={link} rel="noreferrer" target="_blank">
                    {button}
                </a>
            );
        }
        return <Link to={{ pathname: link }}>{button}</Link>;
    }

    return button;
}

ActionBanner.PrimaryAction = PrimaryAction;
ActionBanner.SecondaryAction = SecondaryAction;

export default ActionBanner;
